<template>
<div class="page dd-page">
  <BackBtn/>
  <template>
    <div class="d-flex justify-space-between">
      <BreadCrumbs :items="breadcrumbs"/>
      <v-btn
        v-if="!isLocked"
        outlined
        x-small
        color="#616161"
        class="dd-action-btn--sm"
        style=" height:32px;"
        @click="settingsModal = true; loadCasesLibrary()"
      >
        <v-icon left>mdi-tune</v-icon>
        Настройки УД
      </v-btn>
    </div>
  </template>
  <!-- Loading -->
  <template v-if="loading">
    <div class="dd-layout mt-3">
      <div class="dd-sidebar">
        <v-skeleton-loader type="list-item-three-line"/>
        <v-skeleton-loader type="list-item-two-line" class="mt-2"/>
      </div>
      <div style="flex:1;display:flex;flex-direction:column;gap:12px;">
        <v-skeleton-loader type="card"/>
        <v-skeleton-loader type="card"/>
      </div>
    </div>
  </template>

  <!-- Not found -->
  <template v-else-if="!checklist">
    <div class="d-flex flex-column align-center py-12">
      <v-icon size="64" color="grey lighten-2">mdi-file-search-outline</v-icon>
      <div class="font-md text-gray mt-4">Диагностика не найдена</div>
      <v-btn text color="primary" class="mt-3" @click="$router.push('/diagnostic')">
        Вернуться к списку
      </v-btn>
    </div>
  </template>

  <!-- Main -->
  <template v-else>

    <!-- Locked banner -->
    <v-alert
      v-if="isLocked"
      type="info"
      text
      dense
      color="#616161"
      icon="mdi-lock-outline"
      class="dd-locked-banner mt-3 mb-0"
    >
      Диагностика завершена — редактирование недоступно. Изменить данные можно только через администратора.
    </v-alert>

    <!-- Header card (full width) -->
    <div class="dd-card mb-4 mt-3">
      <div class="dd-header">
        <!-- Fields -->
        <div class="dd-header__fields" :class="{ 'dd-header__fields--editing': participantEditing }">
          <div class="dd-header__row">
            <div class="dd-field" style="flex:2.5">
              <span class="dd-field__label"><v-icon x-small class="mr-1">mdi-account-outline</v-icon>ФИО участника</span>
              <div
                v-if="!participantEditing"
                class="dd-field__value"
                :class="{ 'dd-field__value--editable': !isLocked }"
                :title="isLocked ? '' : 'Нажмите, чтобы изменить'"
                @click="startParticipantEditing"
              >
                <span class="dd-field__text">{{ candidateName }}</span>
                <v-icon v-if="!isLocked" x-small class="dd-field__pencil">mdi-pencil</v-icon>
              </div>
              <v-combobox
                v-else
                v-model="participantForm.candidate"
                :items="candidateOptions"
                :item-text="candidateFullName"
                :loading="candidatesLoading.load"
                dense outlined hide-details autofocus
                no-filter
                no-data-text="Не найдено"
                placeholder="Выберите из списка или введите ФИО"
                class="dd-inline-input"
                @input="onParticipantCandidateInput"
                @update:search-input="onParticipantCandidateSearch"
              />
            </div>
            <div class="dd-field" style="flex:1.8">
              <span class="dd-field__label"><v-icon x-small class="mr-1">mdi-domain</v-icon>Компания</span>
              <div
                v-if="!participantEditing"
                class="dd-field__value"
                :class="{ 'dd-field__value--editable': !isLocked }"
                :title="isLocked ? '' : 'Нажмите, чтобы изменить'"
                @click="startParticipantEditing"
              >
                <span class="dd-field__text">{{ checklist.company || '—' }}</span>
                <v-icon v-if="!isLocked" x-small class="dd-field__pencil">mdi-pencil</v-icon>
              </div>
              <v-text-field
                v-else
                v-model="participantForm.company"
                dense outlined hide-details
                placeholder="Компания"
                class="dd-inline-input"
                @input="queueParticipantSave"
              />
            </div>
            <div class="dd-field" style="flex:1.8">
              <span class="dd-field__label"><v-icon x-small class="mr-1">mdi-briefcase-outline</v-icon>Должность</span>
              <div
                v-if="!participantEditing"
                class="dd-field__value"
                :class="{ 'dd-field__value--editable': !isLocked }"
                :title="isLocked ? '' : 'Нажмите, чтобы изменить'"
                @click="startParticipantEditing"
              >
                <span class="dd-field__text">{{ checklist.position || '—' }}</span>
                <v-icon v-if="!isLocked" x-small class="dd-field__pencil">mdi-pencil</v-icon>
              </div>
              <v-text-field
                v-else
                v-model="participantForm.position"
                dense outlined hide-details
                placeholder="Должность"
                class="dd-inline-input"
                @input="queueParticipantSave"
              />
            </div>
            <div v-if="!isLocked" class="dd-participant-actions">
              <v-btn
                v-if="!participantEditing"
                text x-small color="#9e9e9e" class="dd-participant-btn"
                @click="startParticipantEditing"
              >
                <v-icon x-small left>mdi-pencil</v-icon>
                Изменить
              </v-btn>
              <v-btn
                v-else
                depressed x-small color="#483CB9" dark class="dd-participant-btn"
                @click="stopParticipantEditing"
              >
                <v-icon x-small left>mdi-check</v-icon>
                Готово
              </v-btn>
            </div>
          </div>
          <div class="dd-header__row">
            <div class="dd-field" style="flex:1.5">
              <span class="dd-field__label"><v-icon x-small class="mr-1">mdi-calendar-outline</v-icon>Дата диагностики</span>
              <div class="dd-field__value">{{ formattedDateShort }}</div>
            </div>
            <div class="dd-field" style="flex:1.5">
              <span class="dd-field__label"><v-icon x-small class="mr-1">mdi-account-tie-outline</v-icon>Эксперт</span>
              <div
                v-if="!participantEditing"
                class="dd-field__value"
                :class="{ 'dd-field__value--editable': !isLocked }"
                :title="isLocked ? '' : 'Нажмите, чтобы изменить'"
                @click="startParticipantEditing"
              >
                <span class="dd-field__text">{{ managerName }}</span>
                <v-icon v-if="!isLocked" x-small class="dd-field__pencil">mdi-pencil</v-icon>
              </div>
              <v-autocomplete
                v-else
                v-model="participantForm.manager"
                :items="managerItems"
                item-value="id"
                :item-text="managerFullName"
                :loading="managersLoading.load"
                dense outlined hide-details
                clearable no-filter
                no-data-text="Не найдено"
                placeholder="Выберите эксперта"
                class="dd-inline-input"
                @input="onParticipantManagerInput"
                @update:search-input="onParticipantManagerSearch"
              />
            </div>
            <div class="dd-field" style="flex:2.6">
              <span class="dd-field__label"><v-icon x-small class="mr-1">mdi-clipboard-text-outline</v-icon>Тип диагностики</span>
              <div class="dd-field__value">{{ reportTitle }}</div>
            </div>
          </div>
          <div v-if="participantEditing || participantSaving || participantSaved" class="dd-participant-hint">
            <template v-if="participantSaving">
              <v-progress-circular indeterminate size="12" width="2" color="#483CB9" class="mr-2" />
              <span>Сохраняем…</span>
            </template>
            <template v-else-if="participantSaved">
              <v-icon x-small color="#43a047" class="mr-1">mdi-check-circle</v-icon>
              <span style="color:#43a047;">Изменения сохранены</span>
            </template>
            <template v-else>
              <v-icon x-small color="#483CB9" class="mr-1">mdi-information-outline</v-icon>
              <span>Режим редактирования: изменения сохраняются автоматически</span>
            </template>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="dd-header__actions">
          <v-btn outlined small block color="#616161" class="dd-action-btn" style="height:36px;" :loading="saving" :disabled="isLocked"
                 @click="handleSaveDraft">
            <v-icon small left>mdi-content-save-outline</v-icon>
            Сохранить оценки
          </v-btn>
          <div class="d-flex" style="gap:6px;">
            <v-btn outlined x-small color="#616161" class="dd-action-btn--sm" style="flex:1; height:32px;"
                   @click="shortReportPreview = true">
              <v-icon x-small left>mdi-eye-outline</v-icon>
              Превью краткого
            </v-btn>
            <v-btn outlined x-small color="#616161" class="dd-action-btn--sm" style="flex:1; height:32px;"
                   @click="fullReportPreview = true">
              <v-icon x-small left>mdi-eye-outline</v-icon>
              Превью расшир.
            </v-btn>
          </div>
          <v-btn small block elevation="0" color="success" class="dd-action-btn" style="height:36px;"
                 :loading="pdfGenerating" :disabled="pdfGenerating || !canGenerateReport"
                 @click="handleGenerateShort">
            <v-icon small left>mdi-file-document-outline</v-icon>
            Сформировать короткий отчёт
          </v-btn>
          <v-btn small block elevation="0" dark color="#212121" class="dd-action-btn" style="height:36px;"
                 :loading="pdfGenerating" :disabled="pdfGenerating || !canGenerateReport"
                 @click="handleGenerateFull">
            <v-icon small left>mdi-file-document-multiple-outline</v-icon>
            Сформировать расширенный отчёт
          </v-btn>
          <v-btn
            v-if="!isCompleted"
            small block elevation="0" color="error" class="dd-action-btn"
            style="height:36px;"
            :loading="completing"
            :disabled="!canComplete"
            @click="completeDialog = true"
          >
            <v-icon small left>mdi-check-circle-outline</v-icon>
            Завершить диагностику
          </v-btn>
          <div v-else class="d-flex align-center justify-center" style="background:#E8F5E9; border-radius:7px; height:36px; gap:6px;">
            <v-icon small color="#43a047">mdi-check-circle</v-icon>
            <span style="font-size:13px; font-weight:600; color:#43a047;">Диагностика завершена</span>
          </div>
        </div>
      </div>

      <!-- Stats bar -->
      <div class="dd-stats">
        <div class="dd-stats__item">
          <span class="dd-stats__label">ЗАПОЛНЕНО КОМПЕТЕНЦИЙ</span>
          <span class="dd-stats__value">{{ filledCount }} / {{ competences.length }}</span>
        </div>
        <div class="dd-stats__divider"/>
        <div class="dd-stats__item">
          <span class="dd-stats__label">ТЕКУЩИЙ ОБЩИЙ БАЛЛ</span>
          <span class="dd-stats__value">{{ overallScore !== null ? formatScore2(overallScore) : '—' }}</span>
        </div>
        <div class="dd-stats__divider"/>
        <div class="dd-stats__item">
          <span class="dd-stats__label">ОБЩИЙ ГРЕЙД</span>
          <span class="dd-stats__value" :style="{ color: gradeColor(overallScore) }">{{ gradeLabel(overallScore) }}</span>
        </div>
        <div class="dd-stats__divider"/>
        <div class="dd-stats__item">
          <span class="dd-stats__label">ЗОНА</span>
          <span class="dd-stats__value" :style="{ color: gradeColor(overallScore) }">{{ zoneLabel(overallScore) }}</span>
        </div>
        <div class="dd-stats__divider"/>
        <div class="dd-stats__item">
          <span class="dd-stats__label">СТАТУС</span>
          <span class="dd-stats__value" :style="{ color: isCompleted ? '#43a047' : '#9e9e9e', fontSize: '14px' }">
            {{ isCompleted ? 'Завершена' : 'В процессе' }}
          </span>
        </div>
      </div>
    </div>

    <div class="dd-layout">

      <!-- ─── SIDEBAR ─── -->
      <div class="dd-sidebar">
        <div class="d-flex align-center justify-space-between mb-1">
          <p class="dd-sidebar__section-title mb-0">КОМПЕТЕНЦИИ</p>
          <v-btn
            v-if="!isLocked"
            icon
            x-small
            color="var(--v-primary-base)"
            title="Добавить компетенцию из библиотеки"
            @click="addCompetenceModal = true"
          >
            <v-icon x-small>mdi-plus</v-icon>
          </v-btn>
        </div>

        <draggable
          v-model="orderedCompetences"
          handle=".dd-sidebar__drag-handle"
          :disabled="isLocked"
          :animation="150"
          tag="div"
          @end="onCompetencesReorder"
        >
          <div
            v-for="comp in orderedCompetences"
            :key="comp.id"
            class="dd-sidebar__item"
            :class="{ 'dd-sidebar__item--active': activeComp && activeComp.id === comp.id }"
            @click="activeCompIdx = competences.findIndex(c => c.id === comp.id)"
          >
            <v-icon
              v-if="!isLocked"
              x-small
              class="dd-sidebar__drag-handle flex-shrink-0 mr-1"
              color="#bdbdbd"
              title="Перетащить, чтобы изменить порядок"
              @click.stop
            >
              mdi-drag-vertical
            </v-icon>
            <v-icon x-small class="flex-shrink-0 mr-2" :color="activeComp && activeComp.id === comp.id ? 'var(--v-primary-base)' : '#bdbdbd'">
              mdi-clipboard-list-outline
            </v-icon>
            <span class="dd-sidebar__item-name">{{ comp.name }}</span>
            <span v-if="getCompScore(comp) !== null" class="dd-sidebar__badge" :class="scoreBadgeClass(getCompScore(comp))">
                {{ formatScore1(getCompScore(comp)) }}
              </span>
            <v-btn
              v-if="!isLocked"
              icon
              x-small
              color="error"
              class="ml-2 flex-shrink-0"
              @click.stop="confirmDeleteCompetence(comp)"
              :disabled="deletingCompetence === comp.id"
              title="Удалить компетенцию из чеклиста"
            >
              <v-icon x-small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </draggable>

        <p class="dd-sidebar__section-title mt-4">ИТОГИ</p>
        <div
          class="dd-sidebar__item"
          :class="{ 'dd-sidebar__item--active': activeCompIdx === -1 }"
          @click="activeCompIdx = -1"
        >
          <v-icon x-small class="flex-shrink-0 mr-2" :color="activeCompIdx === -1 ? 'var(--v-primary-base)' : '#bdbdbd'">
            mdi-trophy-outline
          </v-icon>
          <span class="dd-sidebar__item-name">Итоги ассессора</span>
          <v-icon x-small color="#bdbdbd" class="ml-auto flex-shrink-0">mdi-chevron-right</v-icon>
        </div>

        <div class="dd-sidebar__scale mt-4">
          <p class="dd-sidebar__section-title">ШКАЛА ГРЕЙДОВ</p>
          <div v-for="g in GRADES" :key="g.label" class="dd-sidebar__scale-row">
            <span class="dd-sidebar__scale-dot" :style="{ background: g.color }"/>
            <span class="dd-sidebar__scale-name">{{ g.label }}</span>
            <span class="dd-sidebar__scale-range">{{ g.range }}</span>
          </div>
        </div>
      </div>

      <!-- ─── CONTENT ─── -->
      <div class="dd-content">

        <!-- ═══ ACTIVE COMPETENCY ═══ -->
        <template v-if="activeCompIdx >= 0 && activeComp">

          <!-- Competency header -->
          <div class="dd-card mb-3">
            <div class="d-flex align-start" style="gap:14px;">
              <div class="dd-comp-avatar">
                <v-icon color="var(--v-primary-base)" size="22">mdi-clipboard-list-outline</v-icon>
              </div>
              <div style="flex:1; min-width:0;">
                <div class="d-flex align-center flex-wrap mb-1" style="gap:8px;">
                  <span style="font-size:18px; font-weight:700; color:#212121;">{{ activeComp.name }}</span>
                  <span class="dd-chip dd-chip--primary">{{ (activeComp.markers || []).length }} индикатора</span>
                  <span
                    v-if="getCompScore(activeComp) !== null"
                    class="dd-chip"
                    :style="{ background: gradeColorBg(getCompScore(activeComp)), color: gradeColor(getCompScore(activeComp)) }"
                  >{{ gradeLabel(getCompScore(activeComp)) }}</span>
                </div>
                <p v-if="activeComp.description" class="ma-0" style="font-size:13px; color:#757575; line-height:1.6;">
                  {{ activeComp.description }}
                </p>
              </div>
            </div>
          </div>

          <!-- Case tabs -->
          <div class="dd-card mb-3 dd-cases-bar">
            <div class="d-flex align-center mb-2" style="gap:6px;">
              <v-icon small color="grey darken-1">mdi-folder-multiple-outline</v-icon>
              <span class="dd-section-title" style="margin:0; letter-spacing:.07em;">КЕЙСЫ</span>
              <span class="dd-chip dd-chip--primary" style="font-size:11px;">{{ activeCases.length }}</span>
            </div>

            <div v-if="activeCases.length" class="d-flex flex-wrap" style="gap:6px;">
              <div
                v-for="(c, ci) in activeCases"
                :key="c.id"
                class="dd-case-tab"
                :class="{ 'dd-case-tab--active': activeCaseIdx === ci }"
                @click="activeCaseIdx = ci"
              >
                {{ c.name }}
                <span
                  class="dd-case-tab__counter"
                  :class="caseFilledCount(c) === (activeComp.markers || []).length ? 'dd-case-tab__counter--done' : ''"
                >{{ caseFilledCount(c) }}/{{ (activeComp.markers || []).length }}</span>
              </div>
            </div>
            <p v-else class="ma-0 text-caption grey--text">
              <v-icon x-small color="warning" class="mr-1">mdi-alert-outline</v-icon>
              Кейсы не добавлены. Добавьте кейсы в чеклист перед оценкой.
            </p>

            <!-- Collapsible case description -->
            <div v-if="activeCase" class="dd-case-desc mt-3">
              <div class="dd-case-desc__header" @click="caseDescExpanded = !caseDescExpanded">
                <v-icon size="18" color="#009E60">mdi-file-document-outline</v-icon>
                <div style="flex:1; min-width:0;">
                  <div class="dd-case-desc__label">КЕЙС {{ activeCaseIdx + 1 }} · ТЕКСТ ДЛЯ ЗАЧИТЫВАНИЯ</div>
                  <div class="dd-case-desc__title">{{ activeCase.name }}</div>
                </div>
                <v-icon size="18" color="#009E60">{{ caseDescExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
              <div v-if="caseDescExpanded" class="dd-case-desc__body">
                <template v-if="activeCase.description">
                  <a
                    v-if="descriptionCaseisLink"
                    :href="activeCase.description"
                    target="_blank"
                  >
                    Ссылка на кейс
                  </a>
                  <span v-else>{{ activeCase.description }}</span>
                </template>
                <em v-else style="color:#9e9e9e;">Текст кейса не задан. Добавьте в «Настройки УД → Кейсы».</em>
              </div>
            </div>
          </div>

          <!-- Indicators -->
          <template v-if="activeComp.markers && activeComp.markers.length">
            <template v-if="activeCase">
              <div
                v-for="(marker, mIdx) in activeCompMarkers"
                :key="marker.id || mIdx"
                class="dd-card mb-3"
              >
                <div class="d-flex justify-space-between align-start mb-3">
                  <div>
                    <p class="dd-indicator-label">ИНДИКАТОР {{ mIdx + 1 }}</p>
                    <p class="dd-indicator-title">{{ marker.name }}</p>
                  </div>
                  <div class="text-right flex-shrink-0 ml-4">
                    <p class="dd-indicator-label">БАЛЛ · ЭТОТ КЕЙС</p>
                    <span
                      class="dd-indicator-score"
                      :style="{ color: getMarkerCaseScore(activeComp, marker, activeCase) !== null ? gradeColor(getMarkerCaseScore(activeComp, marker, activeCase)) : '#bdbdbd' }"
                    >{{getMarkerCaseScore(activeComp, marker, activeCase) !== null ? getMarkerCaseScore(activeComp, marker, activeCase) : '—' }}</span>
                  </div>
                </div>

                <!-- 2×2 level grid -->
                <div class="dd-levels">
                  <div
                    v-for="lvl in 4"
                    :key="lvl - 1"
                    class="dd-level"
                    :class="{
                      'dd-level--selected': getMarkerCaseScore(activeComp, marker, activeCase) === lvl - 1,
                      'dd-level--locked': isLocked,
                    }"
                    @click="setScore(activeComp, marker, activeCase, lvl - 1)"
                  >
                    <span class="dd-level__num">{{ lvl - 1 }}</span>
                    <span class="dd-level__desc">{{ getLevelDesc(marker, lvl - 1) || '—' }}</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="dd-card mb-3 d-flex align-center py-4" style="gap:8px; color:#9e9e9e; font-size:13px;">
              <v-icon color="grey lighten-1">mdi-folder-open-outline</v-icon>
              Выберите кейс выше для оценки индикаторов
            </div>

            <!-- Summary across all cases -->
            <div class="dd-card mb-3">
              <p class="dd-section-title mb-4">СВОДКА ПО ИНДИКАТОРАМ · СРЕДНЕЕ ПО ВСЕМ КЕЙСАМ</p>
              <div
                v-for="(marker, mIdx) in activeCompMarkers"
                :key="'s' + (marker.id || mIdx)"
                class="mb-4"
              >
                <div class="d-flex justify-space-between align-start mb-2">
                  <div>
                    <p class="dd-indicator-label">ИНДИКАТОР {{ mIdx + 1 }}</p>
                    <p class="dd-indicator-title">{{ marker.name }}</p>
                  </div>
                  <div class="text-right flex-shrink-0 ml-4">
                      <span
                        class="dd-indicator-score"
                        :style="{ color: getMarkerScore(activeComp, marker) !== null ? gradeColor(getMarkerScore(activeComp, marker)) : '#bdbdbd' }"
                      >{{ getMarkerScore(activeComp, marker) !== null ? formatScore2(getMarkerScore(activeComp, marker)) : '—' }}</span>
                    <p class="dd-indicator-label" style="margin:0;">
                      {{ scoredCasesCount(activeComp, marker) }}/{{ activeCases.length }} КЕЙСОВ
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Result by competency -->
            <div class="dd-card dd-card--yellow mb-4">
              <div class="d-flex align-center mb-4" style="gap:8px;">
                <v-icon size="18" color="#f9a825">mdi-star-circle-outline</v-icon>
                <span style="font-size:15px; font-weight:700; color:#212121;">Результат по компетенции</span>
              </div>

              <div class="dd-result-stats mb-4">
                <div class="dd-result-stat">
                  <p class="dd-result-stat__label">СРЕДНИЙ БАЛЛ</p>
                  <p class="dd-result-stat__value">{{ formatScore2(getCompScore(activeComp)) }}</p>
                </div>
                <div class="dd-result-stat">
                  <p class="dd-result-stat__label">ГРЕЙД</p>
                  <p class="dd-result-stat__value" :style="{ color: gradeColor(getCompScore(activeComp)) }">
                    {{ gradeLabel(getCompScore(activeComp)) }}
                  </p>
                </div>
                <div class="dd-result-stat">
                  <p class="dd-result-stat__label">ЗОНА</p>
                  <p class="dd-result-stat__value" :style="{ color: gradeColor(getCompScore(activeComp)) }">
                    {{ zoneLabel(getCompScore(activeComp)) }}
                  </p>
                </div>
              </div>

              <p class="dd-result-sublabel mb-1">Вывод</p>
              <v-textarea
                v-model="compConclusions[activeComp.id]"
                outlined dense hide-details rows="2" auto-grow
                placeholder="Описание результата ..."
                class="dd-textarea dd-textarea--white mb-3"
                :disabled="isLocked"
              />
            </div>

            <!-- Competency notes (strength / growth_area / recommendation) -->
            <template v-if="checklistResult && checklistResult.is_fully_evaluated">
              <div v-for="block in NOTE_BLOCKS" :key="'cn-' + block.kind" class="dd-card mb-3">
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="d-flex align-center" style="gap:10px;">
                    <v-icon :color="block.color" size="22">{{ block.icon }}</v-icon>
                    <div>
                      <div style="font-size:16px; font-weight:700; color:#212121;">{{ block.label }}</div>
                      <div v-if="block.subtitle" style="font-size:12px; color:#9e9e9e;">{{ block.subtitle }}</div>
                    </div>
                  </div>
                </div>

                <!-- Library templates for competence -->
                <template v-if="notesForCompKind(block.kind).length">
                  <p class="dd-section-title mb-3">ШАБЛОН БЛАНКА</p>
                  <div
                    v-for="note in notesForCompKind(block.kind)"
                    :key="note.id"
                    class="dd-note-item"
                    :class="{
                      'dd-note-item--checked': isCompNoteSelected(activeComp.id, block.kind, note.id),
                      'dd-note-item--locked': isLocked,
                    }"
                    :style="isCompNoteSelected(activeComp.id, block.kind, note.id) ? { borderColor: block.color, background: block.bgColor } : {}"
                    @click="!isLocked && toggleCompNoteId(activeComp.id, block.kind, note.id)"
                  >
                    <v-icon v-if="isCompNoteSelected(activeComp.id, block.kind, note.id)" :color="block.color" size="20">mdi-check-circle
                    </v-icon>
                    <v-icon v-else color="#D0D5DD" size="20">mdi-checkbox-blank-circle-outline</v-icon>
                    <span class="dd-note-item__text">{{ note.text }}</span>
                  </div>
                </template>

                <!-- Custom items added -->
                <template
                  v-if="(compCustomItems[activeComp.id] || {})[block.kind] && (compCustomItems[activeComp.id] || {})[block.kind].length">
                  <p class="dd-section-title mt-4 mb-2">ДОБАВЛЕНО ВРУЧНУЮ</p>
                  <div
                    v-for="(item, i) in (compCustomItems[activeComp.id] || {})[block.kind]"
                    :key="'cc' + i"
                    class="dd-note-item dd-note-item--custom"
                    :style="{ borderColor: block.color + '40', background: block.bgColor }"
                  >
                    <v-icon :color="block.color" size="20">mdi-check-circle</v-icon>
                    <span class="dd-note-item__text">{{ item }}</span>
                    <v-btn icon x-small class="ml-auto flex-shrink-0" :disabled="isLocked"
                           @click="removeCompCustomItem(activeComp.id, block.kind, i)">
                      <v-icon x-small color="#bdbdbd">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- Add custom -->
                <template v-if="!isLocked">
                  <p class="dd-section-title mt-4 mb-2">ДОБАВИТЬ СВОЙ ПУНКТ</p>
                  <div class="d-flex" style="gap:8px; align-items:flex-start;">
                    <v-text-field
                      :value="(compCustomInput[activeComp.id] || {})[block.kind] || ''"
                      outlined dense hide-details
                      :placeholder="block.placeholder"
                      class="dd-textarea"
                      style="flex:1;"
                      @input="ensureCompNoteState(activeComp.id); $set(compCustomInput[activeComp.id], block.kind, $event)"
                      @keydown.enter.prevent="addCompCustomItem(activeComp.id, block.kind)"
                    />
                    <v-btn
                      :color="block.color"
                      dark depressed height="40"
                      class="flex-shrink-0 dd-add-btn"
                      @click="addCompCustomItem(activeComp.id, block.kind)"
                    >
                      <v-icon small left>mdi-plus</v-icon>
                      Добавить
                    </v-btn>
                  </div>
                </template>
              </div>
            </template>

            <!-- IDP block -->
            <div class="dd-card mb-4">
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="d-flex align-center" style="gap:8px;">
                  <v-icon size="20" color="#009E60">mdi-target</v-icon>
                  <span style="font-size:15px; font-weight:700; color:#212121;">Индивидуальный план развития</span>
                </div>
                <div v-if="!isLocked" class="d-flex" style="gap:6px;">
                  <v-btn
                    outlined x-small color="#009E60" class="dd-action-btn--sm"
                    :loading="idpSaving"
                    :disabled="!idpItemsLocal.length"
                    @click="saveIdpItems"
                  >
                    <v-icon x-small left>mdi-content-save-outline</v-icon>
                    Сохранить ИПР
                  </v-btn>
                  <v-btn outlined x-small color="#009E60" class="dd-action-btn--sm" @click="addIdpRow">
                    <v-icon x-small left>mdi-plus</v-icon>
                    Добавить строку
                  </v-btn>
                </div>
              </div>
              <div style="font-size:12px; color:#009E60; margin-bottom:16px;">ИПР, который попадёт в расширенный отчёт</div>

              <div v-if="idpItemsLocal.length" class="dd-ipr-table-wrap">
                <table class="dd-table dd-table--ipr">
                  <thead>
                  <tr>
                    <th>Зона развития</th>
                    <th>Что развивать</th>
                    <th>Рекомендованные действия</th>
                    <th style="width:110px;">Срок, дней</th>
                    <th style="width:150px;">Приоритет</th>
                    <th style="width:40px;"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, i) in idpItemsLocal" :key="i">
                    <td>
                      <v-text-field
                        v-model="item.zone"
                        outlined dense hide-details
                        class="dd-ipr-field"
                        :disabled="isLocked"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="item.subject"
                        outlined dense hide-details
                        class="dd-ipr-field"
                        :disabled="isLocked"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="item.actions"
                        outlined dense hide-details
                        class="dd-ipr-field"
                        :disabled="isLocked"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="item.term_in_days"
                        outlined dense hide-details
                        class="dd-ipr-field"
                        type="number"
                        min="0"
                        placeholder="напр. 30"
                        :disabled="isLocked"
                      />
                    </td>
                    <td>
                      <v-select
                        v-model="item.priority"
                        :items="IDP_PRIORITY_OPTIONS"
                        item-value="value"
                        item-text="label"
                        outlined dense hide-details
                        class="dd-ipr-field"
                        :disabled="isLocked"
                      />
                    </td>
                    <td class="text-center">
                      <v-btn icon x-small :disabled="isLocked" @click="removeIdpRow(i)">
                        <v-icon size="18" color="#bdbdbd">mdi-delete-outline</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-else
                class="d-flex align-center justify-center py-5"
                style="color:#9e9e9e; font-size:13px; gap:8px; border:1.5px dashed #e0e0e0; border-radius:8px;"
              >
                <v-icon color="grey lighten-2">mdi-clipboard-list-outline</v-icon>
                Нет пунктов ИПР — нажмите «Добавить строку»
              </div>
            </div>

          </template>

          <div v-else class="dd-card d-flex flex-column align-center py-8">
            <v-icon size="40" color="grey lighten-2">mdi-clipboard-list-outline</v-icon>
            <p style="font-size:13px; color:#9e9e9e; margin-top:8px;">Индикаторы не заданы</p>
          </div>
        </template>

        <!-- ═══ ИТОГИ ═══ -->
        <template v-else>
          <!-- Final profile -->
          <!--          <div class="dd-card mb-4">-->
          <!--            <div class="d-flex align-center mb-1" style="gap:8px;">-->
          <!--              <v-icon color="var(&#45;&#45;v-primary-base)" size="22">mdi-trophy-outline</v-icon>-->
          <!--              <span style="font-size:17px; font-weight:700; color:#212121;">Итоговый профиль руководителя</span>-->
          <!--            </div>-->
          <!--            <p style="font-size:13px; color:#9e9e9e; margin-bottom:20px;">Сводный результат диагностики, рассчитанный автоматически</p>-->

          <!--            <div class="dd-result-stats dd-result-stats&#45;&#45;lg mb-5">-->
          <!--              <div class="dd-result-stat dd-result-stat&#45;&#45;boxed">-->
          <!--                <p class="dd-result-stat__label">ОБЩИЙ БАЛЛ</p>-->
          <!--                <p class="dd-result-stat__value" style="font-size:26px;">{{ overallScore !== null ? formatScore2(overallScore) : '—' }}</p>-->
          <!--              </div>-->
          <!--              <div class="dd-result-stat dd-result-stat&#45;&#45;boxed">-->
          <!--                <p class="dd-result-stat__label">ОБЩИЙ ГРЕЙД</p>-->
          <!--                <p class="dd-result-stat__value" style="font-size:26px;" :style="{ color: gradeColor(overallScore) }">-->
          <!--                  {{ gradeLabel(overallScore) }}</p>-->
          <!--              </div>-->
          <!--              <div class="dd-result-stat dd-result-stat&#45;&#45;boxed">-->
          <!--                <p class="dd-result-stat__label">ЗОНА</p>-->
          <!--                <p class="dd-result-stat__value" style="font-size:26px;" :style="{ color: gradeColor(overallScore) }">-->
          <!--                  {{ zoneLabel(overallScore) }}</p>-->
          <!--              </div>-->
          <!--              <div class="dd-result-stat dd-result-stat&#45;&#45;boxed">-->
          <!--                <p class="dd-result-stat__label">ЗАПОЛНЕНО</p>-->
          <!--                <p class="dd-result-stat__value" style="font-size:26px;">{{ filledCount }} / {{ competences.length }}</p>-->
          <!--              </div>-->
          <!--            </div>-->

          <!--            &lt;!&ndash; Competency table &ndash;&gt;-->
          <!--            <table class="dd-table">-->
          <!--              <thead>-->
          <!--              <tr>-->
          <!--                <th>Компетенция</th>-->
          <!--                <th>Балл</th>-->
          <!--                <th>Грейд</th>-->
          <!--                <th>Зона</th>-->
          <!--                <th>Ключевая рекомендация</th>-->
          <!--              </tr>-->
          <!--              </thead>-->
          <!--              <tbody>-->
          <!--              <tr v-for="(comp, idx) in competences" :key="comp.id || idx">-->
          <!--                <td>{{ comp.name }}</td>-->
          <!--                <td style="font-weight:600;">{{ getCompScore(comp) !== null ? formatScore2(getCompScore(comp)) : '—' }}</td>-->
          <!--                <td>-->
          <!--                      <span v-if="getCompScore(comp) !== null" class="dd-chip dd-chip&#45;&#45;sm"-->
          <!--                            :style="{ background: gradeColorBg(getCompScore(comp)), color: gradeColor(getCompScore(comp)) }"-->
          <!--                      >{{ gradeLabel(getCompScore(comp)) }}</span>-->
          <!--                  <span v-else style="color:#9e9e9e;">—</span>-->
          <!--                </td>-->
          <!--                <td :style="{ color: gradeColor(getCompScore(comp)) }">{{ zoneLabel(getCompScore(comp)) }}</td>-->
          <!--                <td style="color:#9e9e9e;">{{ (compRecs[comp.id] || [])[0] || '—' }}</td>-->
          <!--              </tr>-->
          <!--              </tbody>-->
          <!--            </table>-->
          <!--          </div>-->

          <!-- Assessor notes header -->
          <div class="dd-card mb-3">
            <div class="d-flex align-center mb-1" style="gap:8px;">
              <v-icon color="var(--v-primary-base)" size="22">mdi-clipboard-check-outline</v-icon>
              <span style="font-size:17px; font-weight:700; color:#212121;">Итоги ассессора</span>
            </div>
            <p style="font-size:13px; color:#9e9e9e; margin:0;">
              Отметьте подходящие пункты из шаблона диагностического бланка либо добавьте свои. Эти разделы попадут в финальный отчёт.
            </p>
          </div>

          <div v-for="block in NOTE_BLOCKS" :key="block.kind" class="dd-card mb-4">
            <!-- Block header -->
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center" style="gap:10px;">
                <v-icon :color="block.color" size="22">{{ block.icon }}</v-icon>
                <div>
                  <div style="font-size:16px; font-weight:700; color:#212121;">{{ block.label }}</div>
                  <div style="font-size:12px; color:#9e9e9e;">{{ block.subtitle }}</div>
                </div>
              </div>
            </div>

            <!-- Library templates -->
            <template v-if="notesForKind(block.kind).length">
              <p class="dd-section-title mb-3">ШАБЛОН БЛАНКА</p>
              <div
                v-for="note in notesForKind(block.kind)"
                :key="note.id"
                class="dd-note-item"
                :class="{
                  'dd-note-item--checked': selectedNoteIds[block.kind].includes(note.id),
                  'dd-note-item--locked': isLocked,
                }"
                :style="selectedNoteIds[block.kind].includes(note.id) ? { borderColor: block.color, background: block.bgColor } : {}"
                @click="!isLocked && toggleNoteId(block.kind, note.id)"
              >
                <v-icon v-if="selectedNoteIds[block.kind].includes(note.id)" :color="block.color" size="20">mdi-check-circle</v-icon>
                <v-icon v-else color="#D0D5DD" size="20">mdi-checkbox-blank-circle-outline</v-icon>
                <span class="dd-note-item__text">{{ note.text }}</span>
              </div>
            </template>

            <!-- Custom items added -->
            <template v-if="customItems[block.kind].length">
              <p class="dd-section-title mt-4 mb-2">ДОБАВЛЕНО ВРУЧНУЮ</p>
              <div
                v-for="(item, i) in customItems[block.kind]"
                :key="'c' + i"
                class="dd-note-item dd-note-item--custom"
                :style="{ borderColor: block.color + '40', background: block.bgColor }"
              >
                <v-icon :color="block.color" size="20">mdi-check-circle</v-icon>
                <span class="dd-note-item__text">{{ item }}</span>
                <v-btn icon x-small class="ml-auto flex-shrink-0" :disabled="isLocked" @click="removeCustomItem(block.kind, i)">
                  <v-icon x-small color="#bdbdbd">mdi-close</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Add custom -->
            <template v-if="!isLocked">
              <p class="dd-section-title mt-4 mb-2">ДОБАВИТЬ СВОЙ ПУНКТ</p>
              <div class="d-flex" style="gap:8px; align-items:flex-start;">
                <v-text-field
                  v-model="customInput[block.kind]"
                  outlined dense hide-details
                  :placeholder="block.placeholder"
                  class="dd-textarea"
                  style="flex:1;"
                  @keydown.enter.prevent="addCustomItem(block.kind)"
                />
                <v-btn
                  :color="block.color"
                  dark depressed height="40"
                  class="flex-shrink-0 dd-add-btn"
                  @click="addCustomItem(block.kind)"
                >
                  <v-icon small left>mdi-plus</v-icon>
                  Добавить
                </v-btn>
              </div>
            </template>
          </div>
          <div class="dd-card mb-4">
            <div class="d-flex align-center mb-3" style="gap:10px;">
              <v-icon color="#616161" size="22">mdi-text-box-edit-outline</v-icon>
              <span style="font-size:16px; font-weight:700; color:#212121;">Итоговый комментарий</span>
            </div>
            <div v-if="isLocked" class="dd-conclusion-readonly" v-html="safeConclusionText"/>
            <HtmlContentEditor
              v-else
              v-model="conclusionText"
              title=""
            />
          </div>

        </template>

      </div><!-- /dd-content -->
    </div><!-- /dd-layout -->
  </template>

  <!-- Complete dialog -->
  <v-dialog v-model="completeDialog" max-width="420" persistent>
    <v-card class="rounded-lg">
      <v-card-title class="font-md font-weight-bold pt-5 px-5">Завершить диагностику?</v-card-title>
      <v-card-text class="px-5 font-sm grey--text text--darken-1">
        Диагностика будет помечена как завершённая и переместится в «Финал». Оценки и заметки останутся доступны для редактирования.
      </v-card-text>
      <v-card-actions class="px-5 pb-4" style="gap:8px;">
        <v-spacer/>
        <v-btn text small @click="completeDialog = false">Отмена</v-btn>
        <v-btn small color="success" elevation="0" :loading="completing" @click="handleComplete">Завершить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete dialog -->
  <v-dialog v-model="deleteDialog" max-width="420" persistent>
    <v-card class="rounded-lg">
      <v-card-title class="font-md font-weight-bold pt-5 px-5">Удалить диагностику?</v-card-title>
      <v-card-text class="px-5 font-sm grey--text text--darken-1">
        Это действие нельзя отменить. Диагностика для <strong>{{ candidateName }}</strong> будет удалена безвозвратно.
      </v-card-text>
      <v-card-actions class="px-5 pb-4" style="gap:8px;">
        <v-spacer/>
        <v-btn text small @click="deleteDialog = false">Отмена</v-btn>
        <v-btn small color="error" elevation="0" :loading="deleting" @click="confirmDelete">Удалить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete competence dialog -->
  <v-dialog v-model="deleteCompetenceDialog" max-width="420" persistent>
    <v-card class="rounded-lg">
      <v-card-title class="font-md font-weight-bold pt-5 px-5">Удалить компетенцию?</v-card-title>
      <v-card-text class="px-5 font-sm grey--text text--darken-1">
        Это действие нельзя отменить. Компетенция <strong>{{ competenceToDelete && competenceToDelete.name }}</strong> будет удалена из чеклиста.
        <div class="mt-2 text-caption grey--text">Все оценки и заметки по этой компетенции также будут удалены.</div>
      </v-card-text>
      <v-card-actions class="px-5 pb-4" style="gap:8px;">
        <v-spacer/>
        <v-btn text small @click="deleteCompetenceDialog = false">Отмена</v-btn>
        <v-btn small color="error" elevation="0" :loading="!!deletingCompetence" @click="deleteCompetence">Удалить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <StatusModal
    :show="notification.show"
    :success="notification.success"
    :message="notification.message"
    @update:show="SET_NOTIFICATION({ show: $event, success: notification.success, message: notification.message })"
  />

  <ShortReportPreviewModal
    v-model="shortReportPreview"
    v-bind="shortReportProps"
    :pdf-generating="pdfGenerating"
    @generate-pdf="handleGenerateShort"
  />

  <FullReportPreviewModal
    v-model="fullReportPreview"
    v-bind="fullReportProps"
    :pdf-generating="pdfGenerating"
    @generate-pdf="handleGenerateFull"
  />

  <DiagnosticSettingsModal
    v-model="settingsModal"
    :competencies="competences"
    :library-cases="libraryCases"
    :library-cases-loading="libraryCasesLoading"
    :result-settings="checklist && currentChecklistResultSettings"
    :saving="settingsSaving"
    @save="onSettingsSave"
  />

  <AddCompetenceModal
    v-model="addCompetenceModal"
    :competencies="competences"
    :adding-id="addingCompetenceTemplateId"
    @add="handleAddCompetence"
  />

</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import BackBtn from '@/components/common/BackBtn.vue';
import BreadCrumbs from '@/components/ui/BreadCrumbs.vue';
import StatusModal from '@/components/common/StatusModal.vue';
import ShortReportPreviewModal from '@/components/Diagnostics/Modal/ShortReportPreviewModal.vue';
import FullReportPreviewModal from '@/components/Diagnostics/Modal/FullReportPreviewModal.vue';
import DiagnosticSettingsModal from '@/components/Diagnostics/Modal/DiagnosticSettingsModal.vue';
import AddCompetenceModal from '@/components/Diagnostics/Modal/AddCompetenceModal.vue';
import draggable from 'vuedraggable';
import HtmlContentEditor from '@/components/testEditor/htmlEditor/HtmlContentEditor.vue';
import { downloadFileHelper, sanitizeRichHtml } from '@/common/util.js';
import { getGrade, GRADES } from '@/common/diagnosticGrades.js';

const NOTE_BLOCKS = [
  {
    kind: 'strength',
    label: 'Сильные стороны',
    subtitle: '',
    icon: 'mdi-trophy-outline',
    color: '#009E60',
    bgColor: '#F0FFF8',
    placeholder: 'Свой вариант сильной стороны...',
  },
  {
    kind: 'growth_area',
    label: 'Зоны роста',
    subtitle: '',
    icon: 'mdi-trending-up',
    color: '#F9A825',
    bgColor: '#FFFBF0',
    placeholder: 'Свой вариант зоны роста...',
  },
  {
    kind: 'recommendation',
    label: 'Рекомендации',
    subtitle: '',
    icon: 'mdi-lightbulb-outline',
    color: '#483CB9',
    bgColor: '#F0EEFF',
    placeholder: 'Свой вариант рекомендации...',
  },
];

const IDP_PRIORITY_OPTIONS = [
  {
    value: 'high',
    label: 'Высокий',
  },
  {
    value: 'medium',
    label: 'Средний',
  },
  {
    value: 'low',
    label: 'Низкий',
  },
];

export default {
  name: 'DiagnosticDetail',
  components: {
    BackBtn,
    BreadCrumbs,
    StatusModal,
    ShortReportPreviewModal,
    FullReportPreviewModal,
    DiagnosticSettingsModal,
    AddCompetenceModal,
    HtmlContentEditor,
    draggable,
  },

  data: () => ({
    GRADES,
    NOTE_BLOCKS,
    IDP_PRIORITY_OPTIONS,
    activeCompIdx: 0,
    orderedCompetences: [],
    activeCaseIdx: 0,
    scores: {},
    comments: {},
    compConclusions: {},
    compRecs: {},
    selectedNoteIds: {
      strength: [],
      growth_area: [],
      recommendation: [],
    },
    customItems: {
      strength: [],
      growth_area: [],
      recommendation: [],
    },
    customInput: {
      strength: '',
      growth_area: '',
      recommendation: '',
    },
    deleteDialog: false,
    deleting: false,
    deletingCompetence: null,
    competenceToDelete: null,
    deleteCompetenceDialog: false,
    addCompetenceModal: false,
    addingCompetenceTemplateId: null,
    saving: false,
    completeDialog: false,
    completing: false,
    pdfGenerating: false,
    caseDescExpanded: true,
    shortReportPreview: false,
    fullReportPreview: false,
    settingsModal: false,
    participantEditing: false,
    participantSaving: false,
    participantSaved: false,
    participantTimer: null,
    participantSearchTimer: null,
    participantSavedTimer: null,
    managerSearchTimer: null,
    skipParticipantSearch: false,
    skipManagerSearch: false,
    participantForm: {
      candidate: null,
      manager: null,
      company: '',
      position: '',
    },
    settingsSaving: false,
    conclusionText: '',
    idpItemsLocal: [],
    idpSaving: false,
    compSelectedNoteIds: {},
    compCustomItems: {},
    compCustomInput: {},
    compExistingNotes: {},
    compNotesSnapshot: {},
    reportTitle: 'Управленческая диагностика',
    reportDescription: 'Комплексная оценка управленческих компетенций сотрудника с детализацией по индикаторам и индивидуальным планом развития.',
  }),

  computed: {
    ...mapGetters({
      checklist: 'diagnostics/diagnotics/currentChecklist',
      loading: 'diagnostics/diagnotics/currentChecklistLoading',
      candidate: 'diagnostics/diagnotics/currentChecklistCandidate',
      checklistResult: 'diagnostics/diagnotics/currentChecklistResult',
      notification: 'diagnostics/diagnotics/notification',
      libraryNotes: 'diagnostics/diagnotics/libraryNotes',
      libraryCases: 'diagnostics/diagnotics/libraryCases',
      libraryCasesLoading: 'diagnostics/diagnotics/libraryCasesLoading',
      currentChecklistResultSettings: 'diagnostics/diagnotics/currentChecklistResultSettings',
      idpItems: 'diagnostics/diagnotics/idpItems',
      candidateOptions: 'users/candidates/candidates',
      candidatesLoading: 'users/candidates/loading',
      checklistManager: 'diagnostics/diagnotics/currentChecklistManager',
      managerOptions: 'users/managers/managers',
      managersLoading: 'users/managers/loading',
    }),

    safeConclusionText() {
      return sanitizeRichHtml(this.conclusionText) || '—';
    },

    checklistId() {
      return this.$route.params.id;
    },

    isCompleted() {
      return this.checklistResult?.completion_status === 'completed';
    },

    // Решение по итогам совещания: завершённая диагностика полностью заблокирована
    // для редактирования на фронте — менять можно только через бэк.
    isLocked() {
      return this.isCompleted;
    },

    canComplete() {
      if (!this.competences.length) return false;
      // Все индикаторы по всем кейсам должны быть заполнены
      const allScoresFilled = this.competences.every(comp => {
        const markers = comp.markers || [];
        const cases = comp.cases || [];
        if (!markers.length || !cases.length) return true;
        return markers.every(marker =>
          cases.every(caseItem => this.getMarkerCaseScore(comp, marker, caseItem) !== null),
        );
      });
      // Хотя бы одна заметка в итогах ассессора
      const hasNotes = NOTE_BLOCKS.some(block => this.totalSelected(block.kind) > 0);
      return allScoresFilled && hasNotes;
    },

    candidateName() {
      const fromCandidate = this.candidate
        ? `${this.candidate.last_name || ''} ${this.candidate.first_name || ''}`.trim()
        : '';
      return fromCandidate || this.checklist?.candidate_name || 'Неизвестный участник';
    },

    managerName() {
      const m = this.checklistManager;
      const fullName = m ? `${m.last_name || ''} ${m.first_name || ''}`.trim() : '';
      return fullName || '—';
    },

    // Выбранный эксперт может не попасть в текущую выдачу поиска — подмешиваем его
    managerItems() {
      const items = [...(this.managerOptions || [])];
      const current = this.checklistManager;
      if (current && !items.find(m => m.id === current.id)) items.unshift(current);
      return items;
    },

    formattedDateShort() {
      if (!this.checklist?.created_at) return '—';
      return new Date(this.checklist.created_at).toLocaleDateString('ru-RU');
    },

    competences() {
      return this.checklist?.competences || [];
    },
    activeComp() {
      return this.competences[this.activeCompIdx] || null;
    },
    // Бэк отдаёт markers не гарантированно в порядке заведения — сортируем по id
    // (id присваивается по возрастанию в момент создания индикатора), чтобы
    // порядок был стабильным и совпадал с тем, в котором их завели, а не «по баллам».
    activeCompMarkers() {
      return [...(this.activeComp?.markers || [])].sort((a, b) => a.id - b.id);
    },
    activeCases() {
      return this.activeComp?.cases || [];
    },
    activeCase() {
      return this.activeCases[this.activeCaseIdx] || null;
    },

    overallScore() {
      if (this.checklistResult?.final_score != null) {
        return parseFloat(this.checklistResult.final_score);
      }
      const scored = this.competences.filter(c => this.getCompScore(c) !== null);
      if (!scored.length) return null;
      return scored.reduce((s, c) => s + this.getCompScore(c), 0) / scored.length;
    },

    filledCount() {
      return this.competences.filter(c => this.getCompScore(c) !== null).length;
    },

    breadcrumbs() {
      return [
        {
          title: 'Управленческие диагностики',
          url: '/diagnostic',
        },
        { title: this.candidateName },
      ];
    },

    canGenerateReport() {
      return !!this.checklistResult?.id;
    },

    shortReportProps() {
      const rows = this.competences.map(comp => {
        const score = this.getCompScore(comp);
        const grade = score !== null ? this.gradeLabel(score) : '—';
        const color = score !== null ? this.gradeColor(score) : '#9e9e9e';
        const bg = score !== null ? this.gradeColorBg(score) : '#f5f5f5';
        return {
          id: comp.id,
          name: comp.name,
          score,
          scoreFormatted: score !== null ? Number(score)
            .toFixed(2) : '—',
          gradeLabel: grade,
          gradeColor: color,
          gradeBg: bg,
        };
      });

      const noteText = (kind) => {
        const fromLib = this.selectedNoteIds[kind]
          .map(id => this.libraryNotes.find(n => n.id === id)?.text)
          .filter(Boolean);
        return [...fromLib, ...this.customItems[kind]];
      };

      return {
        candidateName: this.candidateName,
        company: this.checklist?.company || '—',
        position: this.checklist?.position || '—',
        manager: this.managerName,
        date: this.formattedDateShort,
        overallScore: this.overallScore,
        competencyRows: rows,
        strengths: noteText('strength'),
        growthAreas: noteText('growth_area'),
        recommendations: noteText('recommendation'),
        expertConclusion: this.conclusionText,
      };
    },

    fullReportProps() {
      const noteText = (kind) => {
        const fromLib = this.selectedNoteIds[kind]
          .map(id => this.libraryNotes.find(n => n.id === id)?.text)
          .filter(Boolean);
        return [...fromLib, ...this.customItems[kind]];
      };

      const competencyDetails = this.competences.map(comp => {
        const score = this.getCompScore(comp);
        // Тот же порядок, что и в самом чек-листе (по id, а не по баллам) — см. activeCompMarkers.
        const indicators = [...(comp.markers || [])].sort((a, b) => a.id - b.id).map(marker => {
          const mScore = this.getMarkerScore(comp, marker);
          const grade = getGrade(mScore);
          return {
            id: marker.id,
            name: marker.name,
            score: mScore,
            scoreFormatted: mScore !== null ? Number(mScore)
              .toFixed(1) : '—',
            scoreColor: grade?.color || '#9e9e9e',
          };
        });
        return {
          id: comp.id,
          name: comp.name,
          description: comp.description || '',
          comment: this.compConclusions[comp.id] || '',
          score,
          scoreFormatted: score !== null ? Number(score)
            .toFixed(2) : '—',
          gradeLabel: score !== null ? this.gradeLabel(score) : '—',
          gradeColor: score !== null ? this.gradeColor(score) : '#9e9e9e',
          gradeBg: score !== null ? this.gradeColorBg(score) : '#f5f5f5',
          indicators,
        };
      });

      return {
        candidateName: this.candidateName,
        company: this.checklist?.company || '—',
        position: this.checklist?.position || '—',
        manager: this.managerName,
        date: this.formattedDateShort,
        overallScore: this.overallScore,
        strengths: noteText('strength'),
        growthAreas: noteText('growth_area'),
        recommendations: noteText('recommendation'),
        expertConclusion: this.conclusionText,
        competencyDetails,
        resultSettings: this.currentChecklistResultSettings,
        iprRows: this.idpItemsLocal.map(item => ({
          zone: item.zone,
          whatToDevelop: item.subject,
          actions: item.actions,
          deadline: item.term_in_days,
          priority: {
            high: 'Высокий',
            medium: 'Средний',
            low: 'Низкий',
          }[item.priority] || item.priority,
        })),
      };
    },
    descriptionCaseisLink() {
      return this.activeCase.description?.startsWith('https');
    },
  },

  watch: {
    activeCompIdx() {
      this.activeCaseIdx = 0;
      this.caseDescExpanded = true;
    },
    activeCaseIdx() {
      this.caseDescExpanded = true;
    },
    activeComp(comp) {
      if (comp) this.ensureCompNoteState(comp.id);
    },
    checklistResult(result, oldResult) {
      // Восстанавливаем состояние только при первичной загрузке (null → result).
      // При обновлении result после сохранения не трогаем локальный UI-стейт,
      // чтобы не сбросить заполненные оценки и выбранные итоги ассессора.
      if (result && !oldResult) this.loadScoresFromResult(result);
    },
    // Локальная копия для drag-n-drop (vuedraggable требует v-model, а competences — computed из стора).
    competences: {
      immediate: true,
      handler(list) {
        this.orderedCompetences = [...list];
      },
    },
  },

  async mounted() {
    await Promise.all([this.loadChecklist(this.checklistId), this.loadNotesLibrary()]);
  },

  beforeDestroy() {
    clearTimeout(this.participantTimer);
    clearTimeout(this.participantSearchTimer);
    clearTimeout(this.participantSavedTimer);
    clearTimeout(this.managerSearchTimer);
  },

  methods: {
    ...mapMutations({ SET_NOTIFICATION: 'diagnostics/diagnotics/SET_NOTIFICATION' }),
    ...mapActions({
      loadChecklist: 'diagnostics/diagnotics/loadChecklist',
      updateChecklist: 'diagnostics/diagnotics/updateChecklist',
      loadCandidates: 'users/candidates/loadCandidates',
      setCandidateFilterName: 'users/candidates/setFilterName',
      loadManagersFilter: 'users/managers/loadManagersFilter',
      setManagerFilterName: 'users/managers/setFilterName',
      deleteChecklist: 'diagnostics/diagnotics/deleteChecklist',
      deleteChecklistCompetence: 'diagnostics/diagnotics/deleteChecklistCompetence',
      addChecklistCompetence: 'diagnostics/diagnotics/addChecklistCompetence',
      reorderChecklistCompetences: 'diagnostics/diagnotics/reorderChecklistCompetences',
      createChecklistResult: 'diagnostics/diagnotics/createChecklistResult',
      bulkEvaluate: 'diagnostics/diagnotics/bulkEvaluate',
      saveResultNotes: 'diagnostics/diagnotics/saveResultNotes',
      loadNotesLibrary: 'diagnostics/diagnotics/loadNotesLibrary',
      completeResult: 'diagnostics/diagnotics/completeResult',
      saveCompetenceComment: 'diagnostics/diagnotics/saveCompetenceComment',
      loadCasesLibrary: 'diagnostics/diagnotics/loadCasesLibrary',
      addChecklistCase: 'diagnostics/diagnotics/addChecklistCase',
      removeChecklistCase: 'diagnostics/diagnotics/removeChecklistCase',
      updateResultSettings: 'diagnostics/diagnotics/updateResultSettings',
      updateResult: 'diagnostics/diagnotics/updateResult',
      createIdpItem: 'diagnostics/diagnotics/createIdpItem',
      updateIdpItem: 'diagnostics/diagnotics/updateIdpItem',
      removeIdpItem: 'diagnostics/diagnotics/removeIdpItem',
      loadIdpItems: 'diagnostics/diagnotics/loadIdpItems',
      saveCompetenceNotes: 'diagnostics/diagnotics/saveCompetenceNotes',
      generateDiagnosticPdf: 'diagnostics/diagnotics/generateDiagnosticPdf',
    }),

    caseFilledCount(caseItem) {
      if (!this.activeComp) return 0;
      return (this.activeComp.markers || []).filter(
        marker => this.getMarkerCaseScore(this.activeComp, marker, caseItem) !== null,
      ).length;
    },

    markerKey(comp, marker) {
      return `${comp.id}_${marker.id}`;
    },
    markerCaseKey(comp, marker, caseItem) {
      return `${comp.id}_${marker.id}_${caseItem.id}`;
    },

    getMarkerCaseScore(comp, marker, caseItem) {
      const v = this.scores[this.markerCaseKey(comp, marker, caseItem)];
      return v !== undefined ? v : null;
    },

    getMarkerScore(comp, marker) {
      const serverResults = this.parseJson(this.checklistResult?.marker_results);
      if (Array.isArray(serverResults)) {
        const found = serverResults.find(r => r.marker === marker.id);
        if (found != null) return parseFloat(found.score);
      }
      // Fallback: client-side from current unsaved scores
      const cases = comp.cases || [];
      if (!cases.length) return null;
      const scored = cases.filter(c => this.getMarkerCaseScore(comp, marker, c) !== null);
      if (!scored.length) return null;
      return scored.reduce((s, c) => s + this.getMarkerCaseScore(comp, marker, c), 0) / scored.length;
    },

    scoredCasesCount(comp, marker) {
      return (comp.cases || []).filter(c => this.getMarkerCaseScore(comp, marker, c) !== null).length;
    },

    setScore(comp, marker, caseItem, level) {
      if (this.isLocked) return;
      const key = this.markerCaseKey(comp, marker, caseItem);
      if (this.scores[key] === level) {
        this.$delete(this.scores, key);
      } else {
        this.$set(this.scores, key, level);
      }
    },

    getCompScore(comp) {
      const serverResults = this.parseJson(this.checklistResult?.competence_results);
      if (Array.isArray(serverResults)) {
        const found = serverResults.find(r => r.competence === comp.id);
        if (found != null) return parseFloat(found.score);
      }
      // Fallback: client-side
      if (!comp.markers?.length) return null;
      const scored = comp.markers.filter(m => this.getMarkerScore(comp, m) !== null);
      if (!scored.length) return null;
      return scored.reduce((s, m) => s + this.getMarkerScore(comp, m), 0) / scored.length;
    },

    parseJson(val) {
      if (!val) return null;
      if (typeof val === 'object') return val;
      try {
        return JSON.parse(val);
      } catch (_) {
        return null;
      }
    },

    getLevelDesc(marker, level) {
      return marker[`level_${level}_description`] || '';
    },

    formatScore1(s) {
      return s !== null && s !== undefined ? Number(s)
        .toFixed(1) : '—';
    },
    formatScore2(s) {
      return s !== null && s !== undefined ? Number(s)
        .toFixed(2) : '—';
    },

    gradeLabel(s) {
      return getGrade(s)?.label || '—';
    },
    zoneLabel(s) {
      const g = getGrade(s);
      return g ? `${g.label}` : '—';
    },
    gradeColor(s) {
      return getGrade(s)?.color || '#9e9e9e';
    },
    gradeColorBg(s) {
      return getGrade(s)?.bg || '#f5f5f5';
    },

    scoreBadgeClass(s) {
      if (s === null) return '';
      if (s < 1) return 'dd-sidebar__badge--red';
      if (s < 2) return 'dd-sidebar__badge--orange';
      if (s <= 2.5) return 'dd-sidebar__badge--yellow';
      return 'dd-sidebar__badge--green';
    },

    loadScoresFromResult(result) {
      // Evaluations (may be JSON string)
      let evaluations = this.parseJson(result.evaluations) || result.evaluations || [];
      if (!Array.isArray(evaluations)) evaluations = [];
      if (evaluations.length > 0) {
        const newScores = {};
        evaluations.forEach(ev => {
          for (const comp of this.competences) {
            const marker = (comp.markers || []).find(m => m.id === ev.marker);
            const caseItem = (comp.cases || []).find(c => c.id === ev.case);
            if (marker && caseItem) {
              newScores[this.markerCaseKey(comp, marker, caseItem)] = ev.level;
              break;
            }
          }
        });
        this.scores = newScores;
      }

      // Notes — individual records; reconstruct checkboxes and custom items
      const notes = result.notes || [];
      ['strength', 'growth_area', 'recommendation'].forEach(kind => {
        const kindNotes = notes.filter(n => n.kind === kind);
        // Template selections → restore checked IDs
        this.$set(
          this.selectedNoteIds, kind,
          kindNotes.filter(n => n.source_note_template != null)
            .map(n => n.source_note_template),
        );
        // Custom items → restore text list
        this.$set(
          this.customItems, kind,
          kindNotes.filter(n => n.source_note_template == null)
            .map(n => n.text),
        );
      });

      // Восстанавливаем комментарии компетенций
      const checklistComps = this.checklist.competences || [];
      checklistComps.forEach(cr => {
        if (cr.comment) {
          this.$set(this.compConclusions, cr.id, cr.comment);
        }
      });

      this.conclusionText = this.checklistResult?.conclusion || '';

      this.idpItemsLocal = (result.individual_development_plans || []).map(item => ({ ...item }));

      // Инициализируем состояние заметок для всех компетенций
      checklistComps.forEach(comp => this.ensureCompNoteState(comp.id));

      // Восстанавливаем заметки компетенций из competence_results
      const compResultsList = this.parseJson(result.competence_results) || [];
      if (Array.isArray(compResultsList)) {
        compResultsList.forEach(cr => {
          const notes = cr.notes || [];
          if (!notes.length) return;
          const selected = {
            strength: [],
            growth_area: [],
            recommendation: [],
          };
          const custom = {
            strength: [],
            growth_area: [],
            recommendation: [],
          };
          ['strength', 'growth_area', 'recommendation'].forEach(kind => {
            const kindNotes = notes.filter(n => n.kind === kind);
            selected[kind] = kindNotes.filter(n => n.source_note_template != null)
              .map(n => n.source_note_template);
            custom[kind] = kindNotes.filter(n => n.source_note_template == null)
              .map(n => n.text);
          });
          this.$set(this.compSelectedNoteIds, cr.competence, selected);
          this.$set(this.compCustomItems, cr.competence, custom);
          this.$set(this.compExistingNotes, cr.competence, notes);
        });
      }

      // Снимок исходного состояния заметок — чтобы при сохранении отправлять
      // запросы только по компетенциям, которые реально изменились.
      checklistComps.forEach(comp => {
        this.$set(this.compNotesSnapshot, comp.id, this.serializeCompNotes(comp.id));
      });
    },

    serializeCompNotes(compId) {
      return JSON.stringify({
        sel: this.compSelectedNoteIds[compId] || {},
        cust: this.compCustomItems[compId] || {},
      });
    },

    compNotesChanged(compId) {
      return this.serializeCompNotes(compId) !== (this.compNotesSnapshot[compId] || '{}');
    },

    notesForKind(kind) {
      return this.libraryNotes.filter(n => n.kind === kind && (n.assignment === 'result' || !n.assignment));
    },

    notesForCompKind(kind) {
      return this.libraryNotes.filter(n => n.kind === kind && n.assignment === 'competence');
    },

    ensureCompNoteState(compId) {
      if (!this.compSelectedNoteIds[compId]) {
        this.$set(this.compSelectedNoteIds, compId, {
          strength: [],
          growth_area: [],
          recommendation: [],
        });
      }
      if (!this.compCustomItems[compId]) {
        this.$set(this.compCustomItems, compId, {
          strength: [],
          growth_area: [],
          recommendation: [],
        });
      }
      if (!this.compCustomInput[compId]) {
        this.$set(this.compCustomInput, compId, {
          strength: '',
          growth_area: '',
          recommendation: '',
        });
      }
    },

    toggleCompNoteId(compId, kind, noteId) {
      if (this.isLocked) return;
      this.ensureCompNoteState(compId);
      const arr = this.compSelectedNoteIds[compId][kind];
      const i = arr.indexOf(noteId);
      this.$set(this.compSelectedNoteIds[compId], kind, i >= 0 ? arr.filter(x => x !== noteId) : [...arr, noteId]);
    },

    addCompCustomItem(compId, kind) {
      if (this.isLocked) return;
      this.ensureCompNoteState(compId);
      const text = (this.compCustomInput[compId][kind] || '').trim();
      if (!text) return;
      this.$set(this.compCustomItems[compId], kind, [...this.compCustomItems[compId][kind], text]);
      this.$set(this.compCustomInput[compId], kind, '');
    },

    removeCompCustomItem(compId, kind, i) {
      if (this.isLocked) return;
      this.ensureCompNoteState(compId);
      const arr = [...this.compCustomItems[compId][kind]];
      arr.splice(i, 1);
      this.$set(this.compCustomItems[compId], kind, arr);
    },

    compTotalSelected(compId, kind) {
      const sel = (this.compSelectedNoteIds[compId] || {})[kind] || [];
      const cust = (this.compCustomItems[compId] || {})[kind] || [];
      return sel.length + cust.length;
    },

    isCompNoteSelected(compId, kind, noteId) {
      return ((this.compSelectedNoteIds[compId] || {})[kind] || []).includes(noteId);
    },

    totalSelected(kind) {
      return this.selectedNoteIds[kind].length + this.customItems[kind].length;
    },

    toggleNoteId(kind, id) {
      if (this.isLocked) return;
      const arr = this.selectedNoteIds[kind];
      const i = arr.indexOf(id);
      if (i >= 0) {
        this.$set(this.selectedNoteIds, kind, arr.filter(x => x !== id));
        return;
      }
      this.$set(this.selectedNoteIds, kind, [...arr, id]);
    },

    addCustomItem(kind) {
      if (this.isLocked) return;
      const text = (this.customInput[kind] || '').trim();
      if (!text) return;
      this.$set(this.customItems, kind, [...this.customItems[kind], text]);
      this.$set(this.customInput, kind, '');
    },

    removeCustomItem(kind, i) {
      if (this.isLocked) return;
      const arr = [...this.customItems[kind]];
      arr.splice(i, 1);
      this.$set(this.customItems, kind, arr);
    },

    async onSettingsSave({
                           adds,
                           removes,
                           selectedModules,
                         }) {
      const hasCaseChanges = adds.length || removes.length;
      const hasModuleChanges = selectedModules !== undefined;
      if (!hasCaseChanges && !hasModuleChanges) return;
      this.settingsSaving = true;
      try {
        const requests = [
          ...adds.map(a => this.addChecklistCase(a)),
          ...removes.map(r => this.removeChecklistCase(r)),
        ];
        if (hasModuleChanges) requests.push(this.updateResultSettings(selectedModules));
        await Promise.all(requests);
        await this.loadChecklist(this.checklistId);
        this.settingsModal = false;
        this.SET_NOTIFICATION({
          show: true,
          success: true,
          message: 'Настройки диагностики сохранены',
        });
      } catch {
        this.SET_NOTIFICATION({
          show: true,
          success: false,
          message: 'Не удалось сохранить настройки. Попробуйте позже.',
        });
      } finally {
        this.settingsSaving = false;
      }
    },

    candidateFullName(c) {
      if (typeof c === 'string') return c;
      return `${c.last_name || ''} ${c.first_name || ''}`.trim();
    },

    startParticipantEditing() {
      if (this.isLocked || this.participantEditing) return;
      this.participantForm = {
        candidate: this.candidate || this.checklist?.candidate_name || null,
        manager: this.checklist?.manager ?? null,
        company: this.checklist?.company || '',
        position: this.checklist?.position || '',
      };
      this.participantSaved = false;
      this.participantEditing = true;
      this.loadCandidates();
      this.loadManagersFilter();
    },

    stopParticipantEditing() {
      // Досохраняем то, что ещё висит в дебаунсе
      clearTimeout(this.participantTimer);
      this.participantEditing = false;
      this.saveParticipant();
    },

    onParticipantCandidateInput() {
      this.skipParticipantSearch = true;
      this.queueParticipantSave();
    },

    onParticipantCandidateSearch(val) {
      if (val === null || val === undefined) return;
      if (this.skipParticipantSearch) {
        this.skipParticipantSearch = false;
        return;
      }
      clearTimeout(this.participantSearchTimer);
      this.participantSearchTimer = setTimeout(() => {
        this.setCandidateFilterName(val);
      }, 400);
    },

    managerFullName(m) {
      return `${m.last_name || ''} ${m.first_name || ''}`.trim();
    },

    onParticipantManagerInput() {
      this.skipManagerSearch = true;
      // Не сохраняем при очистке поля — дождёмся выбора нового эксперта
      // либо явного нажатия «Готово» (stopParticipantEditing вызовет saveParticipant напрямую).
      if (this.participantForm.manager === null) return;
      this.queueParticipantSave();
    },

    onParticipantManagerSearch(val) {
      if (val === null || val === undefined) return;
      if (this.skipManagerSearch) {
        this.skipManagerSearch = false;
        return;
      }
      clearTimeout(this.managerSearchTimer);
      this.managerSearchTimer = setTimeout(() => {
        this.setManagerFilterName(val);
      }, 400);
    },

    queueParticipantSave() {
      this.participantSaved = false;
      clearTimeout(this.participantTimer);
      this.participantTimer = setTimeout(() => {
        this.saveParticipant();
      }, 800);
    },

    participantPayload() {
      const { candidate } = this.participantForm;
      // v-autocomplete с item-value="id" возвращает число (ID) при выборе из списка.
      // Бэк ожидает только ID менеджера. Если вдруг придёт объект — возьмём .id.
      const mgr = this.participantForm.manager;
      const managerId = mgr && typeof mgr === 'object' ? mgr.id : mgr;
      const payload = {
        manager: managerId ?? null,
        company: (this.participantForm.company || '').trim(),
        position: (this.participantForm.position || '').trim(),
      };
      // v-combobox отдаёт объект при выборе из списка и строку при ручном вводе.
      // PATCH — частичное обновление: бэк трогает только переданные поля.
      // Если раньше был привязан candidate (id), а теперь ввели строку,
      // нужно явно обнулить candidate, иначе старая привязка останется.
      // Поэтому всегда шлём ОБА ключа: активный со значением, второй — пустым.
      if (candidate && typeof candidate === 'object') {
        payload.candidate = candidate.id;
        payload.candidate_name = '';
      } else {
        payload.candidate = null;
        payload.candidate_name = (candidate || '').trim();
      }
      return payload;
    },

    participantUnchanged(payload) {
      const currentName = this.checklist?.candidate_name || '';
      const currentCandidateId = this.checklist?.candidate ?? null;
      // Compare manager by ID since v-autocomplete returns object but checklist.manager is number
      const currentManagerId = this.checklist?.manager ?? null;
      const payloadManagerId = payload.manager ?? null;
      return payload.company === (this.checklist?.company || '')
        && payload.position === (this.checklist?.position || '')
        && payloadManagerId === currentManagerId
        && (payload.candidate ?? null) === currentCandidateId
        && (payload.candidate_name || '') === currentName;
    },

    async saveParticipant() {
      if (this.isLocked) return;
      const payload = this.participantPayload();
      // ФИО обязательно в любом виде — пустое значение бэк не примет
      if (!payload.candidate && !payload.candidate_name) return;
      if (this.participantUnchanged(payload)) {
        return;
      }
      this.participantSaving = true;
      try {
        await this.updateChecklist({
          id: this.checklistId,
          data: payload,
          silent: true,
        });
        this.participantSaved = true;
        // Прячем отметку "сохранено", чтобы она не висела постоянно
        clearTimeout(this.participantSavedTimer);
        this.participantSavedTimer = setTimeout(() => {
          this.participantSaved = false;
        }, 2500);
      } catch (err) {
        console.error('[saveParticipant] ERROR:', err);
        // уведомление показывает экшен стора
      } finally {
        this.participantSaving = false;
      }
    },

    async handleSaveDraft() {
      if (this.saving || this.isLocked) return;
      this.saving = true;
      try {
        const evaluations = [];
        for (const comp of this.competences) {
          for (const marker of (comp.markers || [])) {
            for (const caseItem of (comp.cases || [])) {
              const level = this.getMarkerCaseScore(comp, marker, caseItem);
              if (level !== null) {
                evaluations.push({
                  marker: marker.id,
                  case: caseItem.id,
                  level,
                });
              }
            }
          }
        }
        const hasEvaluations = evaluations.length > 0;

        const kinds = {
          strength: {
            templateIds: [...this.selectedNoteIds.strength],
            customTexts: [...this.customItems.strength],
          },
          growth_area: {
            templateIds: [...this.selectedNoteIds.growth_area],
            customTexts: [...this.customItems.growth_area],
          },
          recommendation: {
            templateIds: [...this.selectedNoteIds.recommendation],
            customTexts: [...this.customItems.recommendation],
          },
        };
        const hasNotes = Object.values(kinds)
          .some(k => k.templateIds.length || k.customTexts.length);
        const hasComments = Object.keys(this.compConclusions).length > 0;
        const hasConclusionComments = this.conclusionText.length > 0;
        const hasIdp = this.idpItemsLocal.length;
        const hasCompNotes = this.competences.some(comp => {
          const sel = this.compSelectedNoteIds[comp.id] || {};
          const cust = this.compCustomItems[comp.id] || {};
          return ['strength', 'growth_area', 'recommendation'].some(k =>
            (sel[k] || []).length > 0 || (cust[k] || []).length > 0,
          );
        });

        if (!hasEvaluations && !hasNotes && !hasConclusionComments && !hasIdp && !hasCompNotes) {
          this.SET_NOTIFICATION({
            show: true,
            success: false,
            message: 'Нет оценок и заметок для сохранения',
          });
          return;
        }

        let resultId = this.checklistResult?.id;
        if (!resultId) {
          const result = await this.createChecklistResult(this.checklist.id);
          resultId = result.id;
        }

        // Последовательно: сначала оценки, потом заметки.
        if (hasEvaluations) {
          await this.bulkEvaluate({
            id: resultId,
            evaluations,
          });
        }

        if (hasNotes) {
          await this.saveResultNotes({
            resultId,
            kinds,
          });
        }

        if (!hasEvaluations && hasNotes) {
          this.SET_NOTIFICATION({
            show: true,
            success: true,
            message: 'Заметки сохранены',
          });
        }

        if (hasComments) {
          try {
            await Promise.all(
              this.competences
                .filter(item => this.compConclusions[item.id] !== undefined)
                .map(item => this.saveCompetenceComment({
                  id: item.id,
                  comment: this.compConclusions[item.id] || '',
                })),
            );
          } catch (error) {
            // Если любая операция упадёт с ошибкой, все остальные тоже отменятся
          }
        }

        // Отправка итогового комментария
        await this.updateResult({
          resultId,
          data: {
            conclusion: this.conclusionText,
          },
        });

        // Сохраняем заметки только по компетенциям, где они реально изменились
        // (сравнение со снимком исходного состояния, снятым при загрузке).
        const compNotePromises = this.competences
          .filter(comp => this.compNotesChanged(comp.id))
          .map(async comp => {
            const compId = comp.id;
            const sel = this.compSelectedNoteIds[compId] || {};
            const cust = this.compCustomItems[compId] || {};
            const noteKinds = {};
            ['strength', 'growth_area', 'recommendation'].forEach(k => {
              noteKinds[k] = {
                templateIds: [...(sel[k] || [])],
                customTexts: [...(cust[k] || [])],
              };
            });
            const newNotes = await this.saveCompetenceNotes({
              competenceId: compId,
              existingNotes: this.compExistingNotes[compId] || [],
              kinds: noteKinds,
            });
            this.$set(this.compExistingNotes, compId, newNotes || []);
            this.$set(this.compNotesSnapshot, compId, this.serializeCompNotes(compId));
          });
        if (compNotePromises.length) await Promise.all(compNotePromises);
      } catch (_) {
        // уведомление об ошибке выводит стор
      } finally {
        this.saving = false;
      }
    },

    async handleComplete() {
      if (this.completing) return;
      this.completing = true;
      try {
        let resultId = this.checklistResult?.id;
        if (!resultId) {
          const result = await this.createChecklistResult(this.checklist.id);
          resultId = result.id;
        }
        await this.completeResult(resultId);
        this.completeDialog = false;
      } catch (_) {
        // уведомление об ошибке выводит стор
      } finally {
        this.completing = false;
      }
    },

    async handleGenerateShort() {
      await this.generateReport('diagnostic_short');
    },

    async handleGenerateFull() {
      await this.generateReport('diagnostic');
    },

    async generateReport(reportType) {
      if (this.pdfGenerating) return;
      const resultId = this.checklistResult?.id;
      if (!resultId) {
        this.SET_NOTIFICATION({
          show: true,
          success: false,
          message: 'Отчёт можно сформировать только после появления результатов диагностики.',
        });
        return;
      }
      this.pdfGenerating = true;
      try {
        // Данные для отчёта бэк собирает сам по resultId — фронт шлёт только тип и флаги
        const result = await this.generateDiagnosticPdf({
          resultId,
          report_type: reportType,
          logo: true,
          candidate_style: true,
        });
        if (result?.link) {
          downloadFileHelper(result);
        } else {
          throw new Error('Не получен URL файла');
        }
      } catch (e) {
        this.SET_NOTIFICATION({
          show: true,
          success: false,
          message: 'Не удалось сформировать отчёт. Попробуйте позже.',
        });
      } finally {
        this.pdfGenerating = false;
      }
    },

    async saveIdpItems() {
      if (this.isLocked || this.idpSaving || !this.idpItemsLocal.length) return;
      this.idpSaving = true;
      try {
        let resultId = this.checklistResult?.id;
        if (!resultId) {
          const result = await this.createChecklistResult(this.checklist.id);
          resultId = result.id;
        }
        await Promise.all(
          this.idpItemsLocal.map(async item => {
            const payload = {
              zone: item.zone,
              subject: item.subject,
              actions: item.actions,
              term_in_days: parseInt(item.term_in_days, 10) || 0,
              priority: item.priority,
            };
            if (item.id) {
              await this.updateIdpItem({ id: item.id, ...payload });
            } else {
              const created = await this.createIdpItem({ checklist_result: resultId, ...payload });
              this.$set(item, 'id', created.id);
            }
          }),
        );
        this.SET_NOTIFICATION({
          show: true,
          success: true,
          message: 'ИПР сохранён',
        });
      } catch (_) {
        // уведомление об ошибке выводит стор
      } finally {
        this.idpSaving = false;
      }
    },

    addIdpRow() {
      if (this.isLocked) return;
      this.idpItemsLocal.push({
        zone: '',
        subject: '',
        actions: '',
        term_in_days: '',
        priority: 'high',
      });
    },

    async removeIdpRow(i) {
      if (this.isLocked) return;
      const item = this.idpItemsLocal[i];
      if (item.id) {
        await this.removeIdpItem(item.id);
      }
      this.idpItemsLocal.splice(i, 1);
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        await this.deleteChecklist(this.checklistId);
        this.$router.push('/diagnostic');
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
      }
    },

    async handleAddCompetence(template) {
      if (this.addingCompetenceTemplateId) return;
      this.addingCompetenceTemplateId = template.id;
      try {
        // The action itself commits SET_NOTIFICATION on both success and failure.
        await this.addChecklistCompetence({ checklistId: this.checklistId, template });
        await this.loadChecklist(this.checklistId);
        // select the newly added competence
        const newIdx = this.competences.findIndex(c => c.source_competence_template === template.id);
        if (newIdx !== -1) this.activeCompIdx = newIdx;
      } catch (e) {
        // notification already shown by the store action; nothing else to do here
      } finally {
        this.addingCompetenceTemplateId = null;
      }
    },

    async onCompetencesReorder() {
      if (this.isLocked) return;
      const activeId = this.activeComp ? this.activeComp.id : null;
      try {
        await this.reorderChecklistCompetences(this.orderedCompetences);
      } catch (e) {
        // notification already shown by the store action; nothing else to do here
      } finally {
        if (activeId !== null) {
          this.activeCompIdx = this.competences.findIndex(c => c.id === activeId);
        }
      }
    },

    confirmDeleteCompetence(comp) {
      this.competenceToDelete = comp;
      this.deleteCompetenceDialog = true;
    },

    async deleteCompetence() {
      if (!this.competenceToDelete) return;
      const deletedId = this.competenceToDelete.id;
      const activeId = this.activeComp ? this.activeComp.id : null;
      this.deletingCompetence = deletedId;
      try {
        // The action itself commits SET_NOTIFICATION on both success and failure.
        await this.deleteChecklistCompetence(deletedId);
        if (activeId === deletedId) {
          // the active competence itself was deleted — drop the selection
          this.activeCompIdx = -1;
        } else if (activeId !== null) {
          // keep the same competence selected even though indices shifted
          this.activeCompIdx = this.competences.findIndex(c => c.id === activeId);
        }
      } catch (e) {
        // notification already shown by the store action; nothing else to do here
      } finally {
        this.deletingCompetence = null;
        this.competenceToDelete = null;
        this.deleteCompetenceDialog = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* ─── Layout ─── */
.dd-layout {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: 12px;
}

.dd-content {
  flex: 1;
  min-width: 0;
}

/* ─── Sidebar ─── */
.dd-sidebar {
  width: 218px;
  min-width: 218px;
  flex-shrink: 0;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 14px 8px 14px 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 3px;
  }
}

.dd-sidebar__section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .08em;
  color: #bdbdbd;
  padding: 0 6px;
  margin: 0 0 6px;
}

.dd-sidebar__item {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 7px 8px;
  border-radius: 7px;
  cursor: pointer;
  transition: background .14s;
  color: #424242;
  font-size: 13px;
  line-height: 1.35;

  &:hover:not(&--active) {
    background: #f7f7f7;
  }

  &--active {
    background: #f0eeff;
    color: var(--v-primary-base);
    font-weight: 600;
  }
}

.dd-sidebar__drag-handle {
  cursor: grab;
}

.dd-sidebar__item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
}

.dd-sidebar__badge {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  margin-left: 4px;
  min-width: 28px;
  text-align: center;
  padding: 2px 5px;
  border-radius: 20px;

  &--red {
    color: #e53935;
    background: #ffebee;
  }

  &--orange {
    color: #fb8c00;
    background: #fff3e0;
  }

  &--yellow {
    color: #f9a825;
    background: #fffde7;
  }

  &--green {
    color: #43a047;
    background: #e8f5e9;
  }
}

.dd-sidebar__scale {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

.dd-sidebar__scale-row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 3px 4px;
  font-size: 12px;
  color: #424242;
}

.dd-sidebar__scale-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dd-sidebar__scale-name {
  flex: 1;
  font-size: 12px;
}

.dd-sidebar__scale-range {
  font-size: 11px;
  color: #bdbdbd;
}

/* ─── Card base ─── */
.dd-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .04);

  &--yellow {
    background: #fffde7;
    border-color: #ffe57f;
  }
}

/* ─── Header layout ─── */
.dd-header {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.dd-header__fields {
  flex: 1;
  min-width: 280px;
}

.dd-header__row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.dd-header__actions {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 190px;
  width: 350px;
  flex-shrink: 0;
}

/* ─── Info fields ─── */
.dd-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 100px;
}

.dd-field__label {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #9e9e9e;
  font-weight: 500;
}

/* ─── Инлайн-правка данных участника ─── */
.dd-header__fields--editing {
  background: #F6F5FF;
  box-shadow: 0 0 0 1.5px #D9D5F5;
  border-radius: 9px;
  padding: 10px 12px;
  margin: -10px -12px;
}

.dd-participant-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding-top: 16px;
}

.dd-participant-btn {
  height: 28px !important;
  letter-spacing: 0;
  text-transform: none;
  font-size: 12px;
}

.dd-participant-hint {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: #7a7a7a;
  margin-top: 8px;
  min-height: 16px;
}

.dd-field__value--editable {
  cursor: pointer;
  border-style: dashed;
  justify-content: space-between;
  gap: 8px;
  transition: border-color 0.15s ease, background 0.15s ease;

  .dd-field__pencil {
    opacity: 0;
    flex-shrink: 0;
    transition: opacity 0.15s ease;
  }

  &:hover {
    border-color: #483CB9;
    background: #FAFAFF;

    .dd-field__pencil {
      opacity: 1;
      color: #483CB9;
    }
  }
}

.dd-field__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dd-inline-input {
  ::v-deep .v-input__slot {
    min-height: 36px !important;
    border-radius: 7px;
  }

  ::v-deep input {
    font-size: 13px;
    font-weight: 500;
    color: #212121;
  }

  ::v-deep .v-select__selections {
    font-size: 13px;
    font-weight: 500;
  }
}

.dd-field__value {
  font-size: 13px;
  font-weight: 500;
  color: #212121;
  border: 1.5px solid #e0e0e0;
  border-radius: 7px;
  padding: 8px 12px;
  min-height: 36px;
  background: #fff;
  line-height: 1.4;
  display: flex;
  align-items: center;
}

/* ─── Action buttons ─── */
.dd-action-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-radius: 7px !important;
}

.dd-action-btn--outlined {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  border-radius: 7px !important;
}

.dd-action-btn--sm {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  border-radius: 6px !important;
  height: 30px !important;
}

/* ─── Stats bar ─── */
.dd-stats {
  display: flex;
  align-items: center;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 14px 20px;
  gap: 0;
  flex-wrap: wrap;
}

.dd-stats__item {
  flex: 1;
  min-width: 100px;
  padding: 0 16px;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
}

.dd-stats__label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .07em;
  color: #9e9e9e;
  margin-bottom: 5px;
}

.dd-stats__value {
  font-size: 20px;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
}

.dd-stats__divider {
  width: 1px;
  height: 38px;
  background: #e0e0e0;
  flex-shrink: 0;
}

/* ─── Competency avatar ─── */
.dd-comp-avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f0eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ─── Chips ─── */
.dd-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;

  &--primary {
    background: #f0eeff;
    color: var(--v-primary-base);
  }

  &--sm {
    font-size: 11px;
    padding: 2px 7px;
  }
}

/* ─── Indicator typography ─── */
.dd-indicator-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .07em;
  color: #9e9e9e;
  margin: 0 0 3px;
}

.dd-indicator-title {
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  margin: 0;
}

.dd-indicator-score {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
}

/* ─── Level cards 2×2 ─── */
.dd-levels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
}

.dd-level {
  display: flex;
  flex-direction: column;
  gap: 7px;
  border: 1.5px solid #eeeeee;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color .14s, background .14s;
  background: #fff;
  min-height: 70px;

  &:hover:not(&--selected) {
    border-color: #c5bef5;
    background: #fafafa;
  }

  &--locked {
    cursor: default;
    pointer-events: none;
  }

  &--selected {
    border-color: #43a047;
    background: #f1f8e9;
  }

  &--locked {
    cursor: default;
    opacity: .7;

    &:hover {
      border-color: #eeeeee;
      background: #fff;
    }
  }
}

.dd-level__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: #f0f0f0;
  font-size: 12px;
  font-weight: 700;
  color: #757575;
  flex-shrink: 0;

  .dd-level--selected & {
    background: #43a047;
    color: #fff;
  }
}

.dd-level__desc {
  font-size: 12px;
  color: #424242;
  line-height: 1.5;
  white-space: pre-line;
}

/* ─── Section title ─── */
.dd-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .07em;
  color: #9e9e9e;
  margin: 0;
}

/* ─── Result stats ─── */
.dd-result-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0;

  &--lg {
    gap: 10px;
  }
}

.dd-result-stat {
  flex: 1;
  min-width: 90px;
  padding: 0 20px 0 0;

  p {
    margin: 0;
  }

  &--boxed {
    border: 1.5px solid #e0e0e0;
    border-radius: 9px;
    padding: 12px 16px;
    background: #fff;
    min-width: 110px;
  }
}

.dd-result-stat__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .07em;
  color: #9e9e9e;
  margin-bottom: 5px !important;
}

.dd-result-stat__value {
  font-size: 17px;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
}

.dd-result-sublabel {
  font-size: 12px;
  color: #9e9e9e;
  font-weight: 500;
}

/* ─── Textareas ─── */
.dd-textarea {
  font-size: 13px !important;

  ::v-deep .v-input__slot {
    font-size: 13px;
  }

  ::v-deep .v-input__slot fieldset {
    border-color: #e0e0e0;
  }

  ::v-deep .v-input__slot:hover fieldset {
    border-color: #bdbdbd;
  }

  &--white ::v-deep .v-input__slot {
    background: #fff;
  }
}

.dd-conclusion-readonly {
  min-height: 90px;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  color: #424242;
  background: #fafafa;

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.dd-textarea-label {
  font-size: 12px;
  font-weight: 600;
  color: #424242;
  margin: 0 0 6px;
  display: flex;
  align-items: center;
}

.dd-2col {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 180px;
  }
}

/* ─── Table ─── */
.dd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: inherit;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #9e9e9e;
    letter-spacing: .05em;
    padding: 10px 12px;
    border-bottom: 1.5px solid #eeeeee;
    background: #fafafa;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f5f5f5;
    vertical-align: middle;
    color: #424242;
  }

  tr:last-child td {
    border-bottom: none;
  }

  &__empty {
    text-align: center;
    padding: 24px !important;
    color: #9e9e9e;
    font-size: 13px;
  }

  &--ipr {
    th, td {
      padding: 8px 8px;
    }

    td:last-child {
      text-align: center;
      padding: 4px;
    }
  }
}

/* ─── Note items ─── */
.dd-note-count {
  font-size: 13px;
  font-weight: 600;
  color: #9e9e9e;
  flex-shrink: 0;
}

.dd-note-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1.5px solid #F0F0F0;
  cursor: pointer;
  margin-bottom: 8px;
  transition: border-color .14s, background .14s;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover:not(&--checked):not(&--custom) {
    border-color: #D0D5DD;
    background: #FAFAFA;
  }

  &--custom {
    cursor: default;
    margin-bottom: 6px;
  }

  &--locked {
    cursor: default;
    pointer-events: none;
  }

  &__text {
    font-size: 14px;
    color: #25325F;
    line-height: 1.5;
    flex: 1;
  }
}

.dd-add-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  border-radius: 7px !important;
}

/* ─── Cases bar ─── */
.dd-cases-bar {
  padding: 14px 16px;
}

.dd-case-tab {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1.5px solid #e0e0e0;
  background: #fafafa;
  font-size: 12px;
  font-weight: 500;
  color: #424242;
  cursor: pointer;
  transition: border-color .14s, background .14s, color .14s;

  &:hover:not(&--active) {
    border-color: #bdbdbd;
    background: #f5f5f5;
  }

  &--active {
    border-color: var(--v-primary-base);
    background: #f0eeff;
    color: var(--v-primary-base);
    font-weight: 600;
  }

  &__counter {
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    font-size: 11px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 20px;
    background: #f0f0f0;
    color: #9e9e9e;

    &--done {
      background: #E8F5E9;
      color: #43a047;
    }
  }
}

/* ─── IDP table ─── */
.dd-ipr-table-wrap {
  overflow-x: auto;
}

.dd-ipr-field {
  font-size: 13px !important;

  ::v-deep .v-input__slot {
    min-height: 36px !important;
    font-size: 13px;
  }

  ::v-deep .v-input__slot fieldset {
    border-color: #e0e0e0;
  }

  ::v-deep .v-input__slot:hover fieldset {
    border-color: #bdbdbd;
  }

  ::v-deep .v-select__selection {
    font-size: 13px;
  }
}

/* ─── Case description (collapsible) ─── */
.dd-case-desc {
  border: 1.5px solid #C8ECD8;
  border-radius: 8px;
  background: #F0FFF8;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    user-select: none;
  }

  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    color: #009E60;
    margin-bottom: 2px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #212121;
  }

  &__body {
    padding: 0 14px 12px;
    font-size: 13px;
    color: #424242;
    line-height: 1.6;
    border-top: 1px solid #C8ECD8;
    padding-top: 10px;
  }
}
</style>
